import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Header.scss";
import lightTheme from "../../../Assets/Icons/light.png";
import darkTheme from "../../../Assets/Icons/dark.png";
import menuopen from "../../../Assets/Icons/menu-open.png";
import menuClose from "../../../Assets/Icons/menu-close.png";
import NotificationIcon from "../../../Assets/IconComponents/Notification";
import { useNavigate } from "react-router";
import { useTheme } from "../../../ContextStore/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { handleLogout, userSelector } from "../../../store/features/auth/authSlice";
import { allPermissionSelector, topNavigationPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { headerPermissionTransformer } from "../../../store/DataTransformer/permissionTransformer";
import { Avatar, IconButton, Popover, Tooltip } from "@mui/material";
import LockOpenTwoToneIcon from "@mui/icons-material/LockOpenTwoTone";
import ChevronRightTwoToneIcon from "@mui/icons-material/ChevronRightTwoTone";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import VideoPopOver from "./VideoPopOver";
import { getApi } from "../../../../utils/services";
import { GET_ALL_VIDEOS, GET_CLIENT_LOGO } from "../../../../utils/services/apiPath";
import { v4 as uuidV4 } from "uuid";
import {
  R_APPRENTICE_180,
  R_TOP_NAVIGATION_DARK_MODE,
  R_TOP_NAVIGATION_NOTIFICATIONS,
  R_TOP_NAVIGATION_VIDEO
} from "../../../../utils/permissionReferenceConfig";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import SubHeaderV2 from "../../SubHeader/SubHeaderV2";
import { routerSelector } from "../../../store/routerSlice";
import { BASE_API_V2_URI } from "../../../../utils/constant";
import { errorToast } from "../../../../utils/toastHelper";
import HelpMenu from "./HelpMenu";

const MyAccountPopOver = ({ userData, popOverOpen, setPopOverOpen, manageAccount, onLogout, anchorEl }) => {
  return (
    <Popover
      disableScrollLock
      anchorEl={anchorEl}
      onClose={() => setPopOverOpen(false)}
      open={popOverOpen}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <div className="header_user_popover">
        <div className="header_user_popover_profile">
          <Avatar src={userData?.clientUserProfilePicture} alt="user-icon" />
          <div className="header_user_popover_profile_info">
            <div className="header_user_popover_profile_info_name">
              {userData.firstName} {userData.lastName}
            </div>
            <div className="header_user_popover_profile_info_email">{userData.email}</div>
            <div className="header_user_popover_profile_info_email">{userData?.companyName}</div>
          </div>
        </div>

        <button className="header_user_popover_myAccount" onClick={manageAccount} style={{ border: "none", background: "none" }}>
          My Account
          <ChevronRightTwoToneIcon />
        </button>
        <button className="header_user_popover_logout" onClick={onLogout} style={{ border: "none", background: "none" }}>
          <LockOpenTwoToneIcon sx={{ mr: 1 }} /> Sign Out
        </button>
      </div>
    </Popover>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [showMenu, setShowMenu] = useState(false);
  const [showVideoPopOver, setShowVideoPopOver] = useState(false);
  const [popOverOpen, setPopOverOpen] = useState(false);
  const [videos, setVideos] = useState([]);

  const accountButtonRef = useRef(null);
  const videoButtonRef = useRef(null);
  const [userData] = useState(JSON.parse(localStorage.getItem("user")));

  const dispatch = useDispatch();
  const { permissions } = useSelector(allPermissionSelector);
  const { subModulePermissions = {} } = useSelector(topNavigationPermissionSelector);
  const { apprenticeManagementType } = useSelector(userSelector);
  const {
    [R_TOP_NAVIGATION_DARK_MODE]: darkModePermission,
    [R_TOP_NAVIGATION_NOTIFICATIONS]: notificationPermission,
    [R_TOP_NAVIGATION_VIDEO]: videoPermission
  } = subModulePermissions;
  const apprentice180Config = {
    ...permissions[R_APPRENTICE_180],
    canView: apprenticeManagementType === "Apprenticeship 180" && permissions[R_APPRENTICE_180]?.canView
  };
  const mergedPermissions = { ...permissions, APPRENTICE_180: apprentice180Config };
  const { customReportRoutes, defaultReportRoutes } = useSelector(routerSelector);
  const accesibleTabs = useMemo(
    () => headerPermissionTransformer(mergedPermissions, customReportRoutes, defaultReportRoutes),
    [permissions, customReportRoutes, defaultReportRoutes]
  );

  const handleMenuOpen = () => setShowMenu(status => !status);
  const handleHome = () => navigate("/dashboard");
  const onLogout = () => dispatch(handleLogout());

  const manageAccount = () => {
    setPopOverOpen(false);
    navigate("/myaccount");
  };

  const getVideos = async () => {
    try {
      const result = await getApi(GET_ALL_VIDEOS);
      if (result?.error) {
        errorToast("Failed to load videos.");
        return;
      }

      const videoList = Array.isArray(result) ? result : result?.data ?? [];
      setVideos(videoList.map(item => ({ ...item, id: item?.id ?? uuidV4() })));
    } catch {
      errorToast("Failed to load videos.");
    }
  };
  const { control } = useForm({});

  useEffect(() => {
    getVideos();
  }, []);

  const isLightTheme = theme === "light";

  return (
    <div className="header" data-nr-unmask="true">
      <div className="header_main" data-nr-unmask="true">
        <div className="header_container">
          <div className="header_logo">
            <div className="header_logo_menu-open">
              <IconButton onClick={handleMenuOpen}>
                <img src={showMenu ? menuClose : menuopen} alt="menu-open" />
              </IconButton>
            </div>
            <IconButton onClick={handleHome} sx={{ padding: 0 }}>
              <img src={`${BASE_API_V2_URI.replace("/api", "")}${GET_CLIENT_LOGO}${userData.clientGuid}`} alt="logo" />
            </IconButton>
          </div>

          <div className="header_user">
            {videoPermission.canView && (
              <div className="header_user_sec" ref={videoButtonRef}>
                <Tooltip placement="bottom" title="Video" arrow>
                  <IconButton onClick={() => setShowVideoPopOver(true)}>
                    <OndemandVideoIcon id="video-icon" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
            <VideoPopOver anchorEl={videoButtonRef.current} open={showVideoPopOver} videos={videos} onClose={() => setShowVideoPopOver(false)} />
            {darkModePermission.canView && (
              <div className="header_user_sec">
                <Tooltip placement="bottom" title="Theme Switcher" arrow>
                  <IconButton onClick={toggleTheme}>
                    <img src={isLightTheme ? darkTheme : lightTheme} alt="mode-icon" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
            {notificationPermission.canView && (
              <div className="header_user_sec">
                <Tooltip placement="bottom" title="Notifications" arrow>
                  <IconButton>
                    <NotificationIcon />
                  </IconButton>
                </Tooltip>
              </div>
            )}
            <div className="header_user_sec">
              <HelpMenu />
            </div>
            <div className="header_user_sec" ref={accountButtonRef}>
              <IconButton onClick={setPopOverOpen}>
                <Avatar className="avatarProfile" src={userData.clientUserProfilePicture} alt="user-icon" />
              </IconButton>
            </div>
            <MyAccountPopOver
              userData={userData}
              popOverOpen={popOverOpen}
              setPopOverOpen={setPopOverOpen}
              manageAccount={manageAccount}
              onLogout={onLogout}
              anchorEl={accountButtonRef.current}
            />
          </div>
        </div>
      </div>
      <div className={`header_subheader ${showMenu && "open"}`} data-nr-unmask="true">
        <div className="header_subheader_main" data-nr-unmask="true">{<SubHeaderV2 tabs={accesibleTabs} />}</div>
      </div>
    </div>
  );
};

MyAccountPopOver.propTypes = {
  userData: PropTypes.shape({
    clientUserProfilePicture: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    companyName: PropTypes.string
  }).isRequired,
  popOverOpen: PropTypes.bool.isRequired,
  setPopOverOpen: PropTypes.func.isRequired,
  manageAccount: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  anchorEl: PropTypes.object
};
export default Header;
